<template>
    <div class="change-operator-modal">
        <CoreTwoColumnsRow :showBorder="true" :condensed="true" class="current-operator">
            <template #left>
                <span>{{ $t('§contracts.current_operator') }}</span>
            </template>
            <template #right>
                <span
                    class="current-operator__name"
                    :class="{ 'current-operator__name--placeholder': currentClientIsDummy }"
                >
                    {{ currentClientName }}
                </span>
            </template>
        </CoreTwoColumnsRow>

        <CoreDropdown
            v-model="selectedClientId"
            :options="operatorOptions"
            :placeholder="$t('§contracts.select_operator_placeholder')"
            :disabled="loading || saving || previewing"
            @update:modelValue="onSelect"
        />

        <div v-if="previewing" class="change-operator-modal__loading">
            {{ $t('§contracts.calculating_changes') }}
        </div>
        <div v-else-if="changes !== null" class="change-operator-modal__diff">
            <div v-if="repriceBlocked" class="change-operator-modal__blocked">
                {{ $t('§contracts.operator_change_reprice_blocked') }}
            </div>
            <p v-else>
                {{
                    changes.length
                        ? $t('§contracts.the_following_values_will_change')
                        : $t('§contracts.no_stored_values_change')
                }}
            </p>
            <DataTable
                v-if="changes.length"
                :value="changes"
                responsiveLayout="scroll"
                class="change-operator-modal__table"
            >
                <Column field="label" :header="$t('§contracts.change_field')">
                    <template #body="{ data }">{{ $t(data.label) }}</template>
                </Column>
                <Column field="from" :header="$t('§contracts.change_from')">
                    <template #body="{ data }">{{ formatFrom(data) }}</template>
                </Column>
                <Column field="to" :header="$t('§contracts.change_to')">
                    <template #body="{ data }">{{ formatTo(data) }}</template>
                </Column>
            </DataTable>
            <div v-if="reprices && !repriceBlocked" class="change-operator-modal__reprice">
                {{ $t('§contracts.operator_change_reprices') }}
            </div>
            <div v-if="pricingDrifted && !repriceBlocked" class="change-operator-modal__reprice">
                {{ $t('§contracts.operator_change_pricing_drifted') }}
            </div>
            <div
                v-if="showCrossBorderWarning && !repriceBlocked"
                class="change-operator-modal__cross-border"
            >
                {{ $t('§contracts.operator_country_changed_review_cross_border_transport') }}
            </div>
            <p class="change-operator-modal__note">{{ $t('§contracts.kyc_credit_will_rerun') }}</p>
        </div>

        <div class="modal-actions">
            <CoreButton
                label="§ui_button.confirm"
                variant="primary"
                icon="pi-check"
                :disabled="!canConfirm || saving"
                :loading="saving"
                @click="confirm"
            />
            <CoreButton
                label="§ui_button.cancel"
                variant="tertiary"
                icon="pi-times"
                :disabled="saving"
                @click="closeModal"
            />
        </div>
    </div>
</template>

<script>
import { useModalStore } from '@/stores/modal'
import { useContractStore } from '@/stores/contract'
import ClientService from '@/api/ClientService'
import ContractService from '@/api/ContractService'
import CoreDropdown from '@/components/core/CoreDropdown.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'

export default {
    name: 'ChangeOperatorModal',

    components: { CoreDropdown, CoreButton, CoreTwoColumnsRow, DataTable, Column },

    data() {
        return {
            selectedClientId: null,
            operators: [],
            loading: false,
            previewing: false,
            saving: false,
            changes: null,
            countryChanged: false,
            reprices: false,
            repriceBlocked: false,
            pricingDrifted: false,
            previewToken: null,
        }
    },

    created() {
        this.modalStore = useModalStore()
        this.contractStore = useContractStore()
        const d = this.modalStore.data
        this.contractId = d.contractId
        this.currentClientId = d.currentClientId
        this.currentClientName = d.currentClientName
        this.currentClientIsDummy = d.currentClientIsDummy
        this.currencyCode = d.currencyCode
        this.selectedClientId = d.currentClientId
    },

    async mounted() {
        this.loading = true
        try {
            const { data } = await ClientService.index()
            this.operators = data
        } finally {
            this.loading = false
        }
    },

    computed: {
        operatorOptions() {
            return (this.operators || [])
                .filter((c) => !c.dummy && c.id !== this.currentClientId)
                .map((c) => ({ value: c.id, label: c.name }))
        },

        canConfirm() {
            return (
                this.selectedClientId &&
                this.selectedClientId !== this.currentClientId &&
                this.changes !== null &&
                !this.previewing &&
                !this.repriceBlocked
            )
        },

        showCrossBorderWarning() {
            return this.countryChanged
        },
    },

    methods: {
        closeModal() {
            this.modalStore.setClose()
        },

        async onSelect(id) {
            this.changes = null
            this.countryChanged = false
            this.reprices = false
            this.repriceBlocked = false
            this.pricingDrifted = false
            if (!id || id === this.currentClientId) return
            this.previewing = true
            this.previewToken = id 
            try {
                const { data } = await ContractService.previewOperatorChange(
                    { contract: this.contractId },
                    { client_id: id },
                )
                if (this.previewToken !== id) return
                this.changes = data.changes
                this.countryChanged = !!data.country_changed
                this.reprices = !!data.reprices
                this.repriceBlocked = !!data.reprice_blocked
                this.pricingDrifted = !!data.pricing_drifted
            } catch (error) {
                if (this.previewToken === id)
                    this.contractStore.notification('error', '§ui_message.something_went_wrong')
            } finally {
                if (this.previewToken === id) this.previewing = false
            }
        },

        async confirm() {
            if (!this.canConfirm) return
            this.saving = true

            try {
                await ContractService.updateOperator(
                    { contract: this.contractId },
                    { client_id: this.selectedClientId },
                )
            } catch (error) {
                this.contractStore.notification('error', '§ui_message.update_failed')
                this.saving = false
                return
            }

            this.contractStore.notification('success', '§ui_message.update_successful')
            this.saving = false
            this.closeModal()

            this.contractStore.getContractData({ contract: this.contractId }).catch(() => {})
        },

        formatFrom(currency) {
            return this.formatFigure(currency.from, currency.format)
        },

        formatTo(currency) {
            return this.formatFigure(currency.to, currency.format)
        },

        formatFigure(value, format) {
            if (value === null || value === undefined) return this.$t('§ui.not_applicable')
            if (format === 'currency') return this.displayAsCurrency(value, 2, this.currencyCode)
            if (format === 'percent') return this.displayAsPercent(value, 2)
            return value
        },
    },
}
</script>

<style scoped>
.change-operator-modal {
    padding: 40px;
}

.current-operator {
    margin-bottom: 1.5rem;
}

.current-operator__name {
    font-weight: 600;
    color: var(--color-secondary);
    text-align: right;
    word-break: break-word;
}

.current-operator__name--placeholder {
    font-weight: 500;
    font-style: italic;
    color: var(--color-secondary-200);
}

.change-operator-modal__loading,
.change-operator-modal__diff {
    margin-top: 1.5rem;
}

.change-operator-modal__table {
    margin-top: 0.75rem;
}

.change-operator-modal__blocked {
    padding: 1rem;
    background-color: var(--color-red-100);
    border-left: 4px solid var(--color-error);
    border-radius: 4px;
}

.change-operator-modal__reprice {
    margin-top: 1.5rem;
    padding: 1rem;
    background-color: var(--color-yellow-100);
    border-left: 4px solid var(--color-warning);
    border-radius: 4px;
}

.change-operator-modal__cross-border {
    margin-top: 1.5rem;
    padding: 1rem;
    background-color: var(--color-yellow-100);
    border-left: 4px solid var(--color-warning);
    border-radius: 4px;
}

.change-operator-modal__note {
    margin-top: 1rem;
    font-style: italic;
    color: var(--color-secondary-200);
}

.modal-actions {
    display: flex;
    justify-content: flex-start;
    gap: 1rem;
    margin-top: 4rem;
}
</style>
